// src/pages/Compliance.jsx
import React, { useEffect, useState } from "react";
// import { supabase } from "../supabaseClient";
import supabase from "../supabaseClient";

function Accordion({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-md bg-white">
      <button
        onClick={() => setOpen((s) => !s)}
        className="w-full text-left px-4 py-3 flex items-center justify-between"
        aria-expanded={open}
      >
        <span className="font-semibold">{title}</span>
        <span className="text-sm text-slate-600">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0 text-sm text-slate-700">{children}</div>
      )}
    </div>
  );
}

// Annual compliance audit summary - add one row per financial year
const complianceAudits = [
  { year: "FY 2025-26", status: "Conducted", remarks: "N/A" },
];

export default function Compliance() {
  // STATES FOR DB DATA
  const [channels, setChannels] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [annualTrend, setAnnualTrend] = useState([]);
  const [reportDate, setReportDate] = useState(null);
  const [loading, setLoading] = useState(true);
  // FETCH FROM SUPABASE
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1️⃣ First Table
        const { data: overviewData } = await supabase
          .from("compliance_channels")
          .select("*")
          .order("id");

        // 2️⃣ Monthly table
        const { data: monthlyData } = await supabase
          .from("compliance_monthly ")
          .select("*")
          .order("id", { ascending: true });

        // 3️⃣ Annual table
        const { data: annualData } = await supabase
          .from("compliance_annual")
          .select("*")
          .order("id");

        // 4️⃣ Report Date
        const { data: reportData } = await supabase
          .from("compliance_report")
          .select("*")
          .single();

        if (reportData?.report_date) {
          const d = new Date(reportData.report_date);
          const formatted = d.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
          setReportDate(formatted);
        } else {
          setReportDate(null);
        }

        setChannels(overviewData || []);
        setMonthlyTrend(monthlyData || []);
        setAnnualTrend(annualData || []);
      } catch (err) {
        console.error("Error loading compliance data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const reportDateFormatted = reportDate;

  if (loading) return <p className="p-6">⏳ Loading...</p>;

  return (
    <div className="space-y-1 px-4 md:px-6 lg:px-0 max-w-6xl mx-auto">
      <div className="rounded-lg p-6 bg-white card-soft">
        <h1 className="text-2xl font-semibold h-font">
          Compliance & Investor Charter
        </h1>
        <p className="mt-2 text-slate-600">
          We believe transparency builds trust. Below are key disclosures and
          grievance mechanisms.
        </p>
        <div className="mt-6 grid md:grid-cols-2 gap-3" />
      </div>

      {/* Your full above accordion: A to F — unchanged */}
      {/* 👇 Nothing changed in this entire first section */}
      {/* ---------------------- KEEP AS IS ---------------------- */}

      <section className="space-y-3">
        <Accordion
          title="Investor Charter in respect of Investment Adviser (IA)"
          defaultOpen={false}
        >
          <div className="space-y-3">
            <div>
              <strong>A. Vision and Mission Statements for investors</strong>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Vision: Invest with knowledge & safety.</li>
                <li>
                  Mission: Every investor should be able to invest in right
                  investment products based on their needs, manage and monitor
                  them to meet their goals, access reports and enjoy financial
                  wellness.
                </li>
              </ul>
            </div>

            <div>
              <strong>
                B. Details of business transacted by the Investment Adviser with
                respect to the investors
              </strong>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>
                  To enter into an agreement with the client providing all
                  details including fee details, aspects of Conflict-of-interest
                  disclosure and maintaining confidentiality of information.
                </li>
                <li>
                  To do a proper and unbiased risk – profiling and suitability
                  assessment of the client.
                </li>
                <li>To conduct audit annually.</li>
                <li>To disclose the status of complaints on its website.</li>
                <li>
                  To disclose the name, proprietor name, type of registration,
                  registration number, validity, complete address with telephone
                  numbers and associated SEBI Office details (i.e. Head office/
                  regional/ local Office) on its website.
                </li>
                <li>To employ only qualified and certified employees.</li>
                <li>To deal with clients only from official number.</li>
                <li>
                  To maintain records of interactions, with all clients
                  including prospective clients (prior to onboarding), where any
                  conversation related to advice has taken place.
                </li>
                <li>
                  To ensure that all advertisements are in adherence to the
                  provisions of the Advertisement Code for Investment Advisers.
                </li>
                <li>
                  Not to discriminate in terms of services provided, among
                  clients opting for same/similar products/services offered by
                  investment adviser.
                </li>
              </ul>
            </div>

            <div>
              <strong>
                C. Details of services provided to investors (No Indicative
                Timelines)
              </strong>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Onboarding of Clients</li>
                <li>Sharing of agreement copy</li>
                <li>Completing KYC of clients</li>
                <li>Disclosure to Clients</li>
                <li>
                  To provide full disclosure about its business, affiliations,
                  compensation in the agreement.
                </li>
                <li>
                  To not access client’s accounts or holdings for offering
                  advice.
                </li>
                <li>To disclose the risk profile to the client.</li>
                <li>
                  To disclose any conflict of interest of the investment
                  advisory activities with any other activities of the
                  investment adviser.
                </li>
                <li>
                  To disclose the extent of use of Artificial Intelligence tools
                  in providing investment advisory services.
                </li>
                <li>
                  To provide investment advice to the client based on the
                  risk-profiling of the clients and suitability of the client.
                </li>
                <li>
                  To treat all advisory clients with honesty and integrity.
                </li>
                <li>
                  To make adequate disclosure to the investor of all material
                  facts such as risks, obligations, costs, etc. relating to the
                  products or securities advised by the adviser.
                </li>
                <li>
                  To provide clear guidance and adequate caution notice to
                  clients when providing investment advice for dealing in
                  complex and high-risk financial products/services.
                </li>
                <li>
                  To ensure confidentiality of information shared by clients
                  unless such information is required to be provided in
                  furtherance of discharging legal obligations or a client has
                  provided specific consent to share such information.
                </li>
                <li>
                  To disclose the timelines for the various services provided by
                  the investment adviser to clients and ensure adherence to the
                  said timelines.
                </li>
              </ul>
            </div>

            <div>
              <strong>
                D. Details of grievance redressal mechanism and how to access it
              </strong>
              <div className="mt-2 space-y-2">
                <p>
                  <strong>
                    Mode of filing the complaint with investment adviser:
                  </strong>
                </p>
                <p>
                  In case of any grievance / complaint, an investor may approach
                  the concerned Investment Adviser who shall strive to redress
                  the grievance immediately, but not later than 21 days of the
                  receipt of the grievance.
                </p>

                <p>
                  <strong>
                    Mode of filing the complaint on SCORES or with Investment
                    Adviser Administration and Supervisory Body (IAASB):
                  </strong>
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>
                    SCORES 2.0 (a web based centralized grievance redressal
                    system of SEBI for facilitating effective grievance
                    redressal in time-bound manner) (https://scores.sebi.gov.in)
                  </li>
                  <li>First review done by designated body (IAASB)</li>
                  <li>Second review done by SEBI</li>
                  <li>Email to designated email ID of IAASB</li>
                </ul>

                <p className="mt-2">
                  If the Investor is not satisfied with the resolution provided
                  by the Market Participants, then the Investor has the option
                  to file the complaint/ grievance on SMARTODR platform for its
                  resolution through online conciliation or arbitration.
                </p>

                <p className="mt-2">
                  With regard to physical complaints, investors may send their
                  complaints to:
                  <br />
                  Office of Investor Assistance and Education,
                  <br />
                  Securities and Exchange Board of India,
                  <br />
                  SEBI Bhavan, Plot No. C4-A, ‘G’ Block,
                  <br />
                  Bandra-Kurla Complex, Bandra (E),
                  <br />
                  Mumbai - 400 051
                </p>
              </div>
            </div>

            <div>
              <strong>E. Rights of investors</strong>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Right to Privacy and Confidentiality</li>
                <li>Right to Transparent Practices</li>
                <li>Right to fair and Equitable Treatment</li>
                <li>Right to Adequate Information</li>
                <li>Right to Initial and Continuing Disclosure</li>
                <li>
                  Right to receive information about all the statutory and
                  regulatory disclosures.
                </li>
                <li>Right to Fair & True Advertisement</li>
                <li>
                  Right to Awareness about Service Parameters and Turnaround
                  Times
                </li>
                <li>Right to be informed of the timelines for each service</li>
                <li>Right to be Heard and Satisfactory Grievance Redressal</li>
                <li>Right to have timely redressal</li>
                <li>Right to Suitability of the Financial Products</li>
                <li>
                  Right to Exit from Financial product or service in accordance
                  with the terms of agreement with the investment adviser
                </li>
                <li>
                  Right to receive clear guidance and caution notice when
                  dealing in Complex and High-Risk Financial Products and
                  Services
                </li>
                <li>Additional Rights to vulnerable consumers</li>
                <li>
                  Right to get access to services in a suitable manner even if
                  differently abled
                </li>
                <li>
                  Right to provide feedback on the financial products and
                  services used
                </li>
                <li>
                  Right against coercive, unfair, and one-sided clauses in
                  financial agreements
                </li>
              </ul>
            </div>

            <div>
              <strong>
                F. Expectations from the investors (Responsibilities of
                investors)
              </strong>
              <div className="mt-2">
                <strong>Do’s</strong>
                <ol className="list-decimal pl-5 mt-2 space-y-1">
                  <li>Always deal with SEBI registered Investment Advisers.</li>
                  <li>
                    Ensure that the Investment Adviser has a valid registration
                    certificate.
                  </li>
                  <li>
                    Check for SEBI registration number. Please refer to the list
                    of all SEBI registered Investment Advisers which is
                    available on SEBI website in the following link: SEBI
                    Registered Investment Advisers
                  </li>
                  <li>
                    Pay only advisory fees to your Investment Adviser. Make
                    payments of advisory fees through banking channels only and
                    maintain duly signed receipts mentioning the details of your
                    payments. You may make payment of advisory fees through
                    Centralised Fee Collection Mechanism (CeFCoM) of IAASB if
                    investment adviser has opted for the mechanism.
                  </li>
                  <li>
                    Always ask for your risk profiling before accepting
                    investment advice. Insist that Investment Adviser provides
                    advisory strictly on the basis of your risk profiling and
                    take into account available investment alternatives.
                  </li>
                  <li>
                    Ask all relevant questions and clear your doubts with your
                    Investment Adviser before acting on advice.
                  </li>
                  <li>
                    Assess the risk–return profile of the investment as well as
                    the liquidity and safety aspects before making investments.
                  </li>
                  <li>
                    Insist on getting the terms and conditions in writing duly
                    signed and stamped. Read these terms and conditions
                    carefully particularly regarding advisory fees, advisory
                    plans, category of recommendations etc. before dealing with
                    any Investment Adviser.
                  </li>
                  <li>Be vigilant in your transactions.</li>
                  <li>
                    Approach the appropriate authorities for redressal of your
                    doubts / grievances.
                  </li>
                  <li>
                    Inform SEBI about Investment Advisers offering assured or
                    guaranteed returns.
                  </li>
                  <li>
                    Always be aware that you have the right to exit the service
                    of an Investment Adviser.
                  </li>
                  <li>
                    Always be aware that you have the right to seek
                    clarifications and clear guidance on advice.
                  </li>
                  <li>
                    Always be aware that you have the right to provide feedback
                    to the Investment Adviser in respect of services received.
                  </li>
                  <li>
                    Always be aware that you will not be bound by any clause,
                    prescribed by the investment adviser, which is contravening
                    any regulatory provisions.
                  </li>
                </ol>

                <strong className="mt-3 block">Don’ts</strong>
                <ol className="list-decimal pl-5 mt-2 space-y-1">
                  <li>
                    Don’t fall for stock tips offered under the pretext of
                    investment advice.
                  </li>
                  <li>
                    Do not provide funds for investment to the Investment
                    Adviser.
                  </li>
                  <li>
                    Don’t fall for the promise of indicative or exorbitant or
                    assured returns by the Investment Advisers. Don’t let greed
                    overcome rational investment decisions.
                  </li>
                  <li>
                    Don’t fall prey to luring advertisements or market rumours.
                  </li>
                  <li>
                    Avoid doing transactions only on the basis of phone calls or
                    messages from any Investment adviser or its representatives.
                  </li>
                  <li>
                    Don’t take decisions just because of repeated messages and
                    calls by Investment Advisers.
                  </li>
                  <li>
                    Do not fall prey to limited period discount or other
                    incentive, gifts, etc. offered by Investment advisers.
                  </li>
                  <li>
                    Don’t rush into making investments that do not match your
                    risk-taking appetite and investment goals.
                  </li>
                  <li>
                    Do not share login credential and password of your trading,
                    demat or bank accounts with the Investment Adviser.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </Accordion>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          Grievance Redressal / Escalation Matrix
        </h2>

        <div className="overflow-x-auto rounded-lg border bg-white card-soft">
          <table className="w-full table-auto border-collapse text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-3 border text-left">Details of designation</th>
                <th className="p-3 border text-left">Contact Person Name</th>
                <th className="p-3 border text-left">Address</th>
                <th className="p-3 border text-left">Contact No.</th>
                <th className="p-3 border text-left">Email-ID</th>
                <th className="p-3 border text-left">
                  Working hours when complainant can call
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="odd:bg-white even:bg-slate-50">
                <td className="p-3 border">Customer Care</td>
                <td className="p-3 border">Aman Rakesh Shah</td>
                <td className="p-3 border">
                  Unit No. 129, Gala Industrial Estate, 2nd floor,
                  <br /> Dindayal Upadhyay Marg, Mulund (West), Mumbai – 400080
                </td>
                <td className="p-3 border">+91 9867066755</td>
                <td className="p-3 border">ca.aman.shah@gmail.com</td>
                <td className="p-3 border">
                  Mon-Sat
                  <br />
                  09AM – 05 PM
                </td>
              </tr>

              <tr className="odd:bg-white even:bg-slate-50">
                <td className="p-3 border">Head of Customer Care</td>
                <td className="p-3 border">--</td>
                <td className="p-3 border">--</td>
                <td className="p-3 border">--</td>
                <td className="p-3 border">--</td>
                <td className="p-3 border">--</td>
              </tr>

              <tr className="odd:bg-white even:bg-slate-50">
                <td className="p-3 border">Compliance Officer</td>
                <td className="p-3 border">Aman Rakesh Shah</td>
                <td className="p-3 border">
                  Unit No. 129, Gala Industrial Estate, 2nd floor,
                  <br /> Dindayal Upadhyay Marg, Mulund (West), Mumbai – 400080
                </td>
                <td className="p-3 border">+91 9867066755</td>
                <td className="p-3 border">ca.aman.shah@gmail.com</td>
                <td className="p-3 border">
                  Mon-Sat
                  <br />
                  09AM – 05 PM
                </td>
              </tr>

              <tr className="odd:bg-white even:bg-slate-50">
                <td className="p-3 border">CEO</td>
                <td className="p-3 border">--</td>
                <td className="p-3 border">--</td>
                <td className="p-3 border">--</td>
                <td className="p-3 border">--</td>
                <td className="p-3 border">--</td>
              </tr>

              <tr className="odd:bg-white even:bg-slate-50">
                <td className="p-3 border">Principal Officer</td>
                <td className="p-3 border">Aman Rakesh Shah</td>
                <td className="p-3 border">
                  Unit No. 129, Gala Industrial Estate, 2nd floor,
                  <br /> Dindayal Upadhyay Marg, Mulund (West), Mumbai – 400080
                </td>
                <td className="p-3 border">+91 9867066755</td>
                <td className="p-3 border">ca.aman.shah@gmail.com</td>
                <td className="p-3 border">
                  Mon-Sat
                  <br />
                  09AM – 05 PM
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-sm text-slate-600 mt-3">
          The abovementioned details would facilitate the complainants to
          approach the concerned IA/RA before filing complaint to SEBI. For more
          details go to:
          <br />
          <a
            href="https://www.bseindia.com/markets/MarketInfo/DispNewNoticesCirculars.aspx?page=20241209-41"
            className="text-green-700 underline underline-offset-2"
            target="_blank"
            rel="noreferrer"
          >
            https://www.bseindia.com/markets/MarketInfo/DispNewNoticesCirculars.aspx?page=20241209-41
          </a>
        </p>

        <p className="text-sm text-slate-600 mt-2">
          We aim to resolve all grievances within 21 working days from the date
          of receipt. If your grievance is not resolved within this timeframe,
          you can escalate it to SEBI’s SCORES Platform (SEBI Complaints Redress
          System) – scores.sebi.gov.in. In case you are unsatisfied with the
          resolution provided through our support or the SCORES platform, you
          can access the Online Dispute Resolution (ODR) Portal – smartodr.in.
        </p>

        <p className="text-sm font-semibold text-slate-800 mt-3">
          <strong>SEBI regional/local office address –</strong> SEBI Head Office: Plot No.C4-A,'G' Block, Bandra Kurla Complex, Bandra (East), Mumbai 400051
        </p>
        <p className="text-sm font-semibold text-slate-800 mt-1">
          <strong>Board Numbers:</strong> +91-22-26449000 / 40459000
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          Annexure - B: Complaint Data
        </h2>

        <div className="rounded-lg border bg-white card-soft p-4 overflow-x-auto">
          <h3 className="font-semibold mb-2">
            Complaint Data to be displayed by IAs
          </h3>
          <p className="text-sm text-slate-600 mb-4 ">
            Data for the month ending – {reportDateFormatted}
          </p>

          <table className="table1 a11y-readable w-full table-auto border-collapse text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-2 border">Sr.</th>
                <th className="p-2 border">Received from</th>
                <th className="p-2 border">Pending at the end of last Month</th>
                <th className="p-2 border">Received</th>
                <th className="p-2 border">Resolved*</th>
                <th className="p-2 border">Total Pending#</th>
                <th className="p-2 border">Pending complaints > 3 months</th>
                <th className="p-2 border">Avg Resolution Time^(in days)</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((row, i) => (
                <tr key={row.id} className="odd:bg-white even:bg-slate-50">
                  <td className="p-2 border text-center">{i + 1}</td>
                  <td className="p-2 border">{row.channel}</td>
                  <td className="p-2 border text-center">
                    {row.pending_last_month}
                  </td>
                  <td className="p-2 border text-center">{row.received}</td>
                  <td className="p-2 border text-center">{row.resolved}</td>
                  <td className="p-2 border text-center">{row.pending}</td>
                  <td className="p-2 border text-center">
                    {row.pending_over_3_months}
                  </td>
                  <td className="p-2 border text-center">
                    {row.avg_days || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
            <tr className="bg-slate-100 font-semibold">
              <td className="p-2 border"></td>
              <td className="p-2 border">Grand Total</td>
              <td className="p-2 border text-center">
                {channels.reduce(
                  (a, c) => a + Number(c.pending_last_month || 0),
                  0
                )}
              </td>
              <td className="p-2 border text-center">
                {channels.reduce((a, c) => a + Number(c.received || 0), 0)}
              </td>
              <td className="p-2 border text-center">
                {channels.reduce((a, c) => a + Number(c.resolved || 0), 0)}
              </td>
              <td className="p-2 border text-center">
                {channels.reduce((a, c) => a + Number(c.pending || 0), 0)}
              </td>
              <td className="p-2 border text-center">
                {channels.reduce(
                  (a, c) => a + Number(c.pending_over_3_months || 0),
                  0
                )}
              </td>
              <td className="p-2 border text-center">-</td>
            </tr>
          </table>
          <p className="text-sm text-slate-600 mb-2 italic">
            * Inclusive of complaints of previous months resolved in the current
            month.{" "}
          </p>
          <p className="text-sm text-slate-600 mb-2 italic">
            # Inclusive of complaints pending as on the last day of the month.{" "}
          </p>
          <p className="text-sm text-slate-600 mb-2 italic">
            ^ Average Resolution time is the sum total of time taken to resolve
            each complaint in days, in the current month divided by total number
            of complaints resolved in the current month.{" "}
          </p>
        </div>

        <div className="mt-6 rounded-lg border bg-white card-soft p-4 overflow-x-auto">
          <h3 className="font-semibold mb-2">
            Trend of monthly disposal of complaints (last months){" "}
          </h3>

          <table className="w-full table-auto border-collapse text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-2 border">Sr.</th>
                <th className="p-2 border text-left">Month</th>
                <th className="p-2 border text-center">Carried forward from previous month </th>
                <th className="p-2 border text-center">Received</th>
                <th className="p-2 border text-center">Resolved*</th>
                <th className="p-2 border text-center">Pending#</th>
              </tr>
            </thead>
            <tbody>
              {monthlyTrend.map((m, i) => (
                <tr key={m.id} className="odd:bg-white even:bg-slate-50">
                  <td className="p-2 border text-center">{i + 1}</td>
                  <td className="p-2 border">{m.month_label}</td>
                  <td className="p-2 border text-center">{m.carried}</td>
                  <td className="p-2 border text-center">{m.received}</td>
                  <td className="p-2 border text-center">{m.resolved}</td>
                  <td className="p-2 border text-center">{m.pending}</td>
                </tr>
              ))}
            </tbody>
            <tr className="bg-slate-100 font-semibold">
              <td className="p-2 border"></td>
              <td className="p-2 border">Grand Total</td>
              <td className="p-2 border text-center">
                {monthlyTrend.reduce((a, c) => a + Number(c.carried || 0), 0)}
              </td>
              <td className="p-2 border text-center">
                {monthlyTrend.reduce((a, c) => a + Number(c.received || 0), 0)}
              </td>
              <td className="p-2 border text-center">
                {monthlyTrend.reduce((a, c) => a + Number(c.resolved || 0), 0)}
              </td>
              <td className="p-2 border text-center">
                {monthlyTrend.reduce((a, c) => a + Number(c.pending || 0), 0)}
              </td>
            </tr>
          </table>
          <p className="text-sm text-slate-600 mb-2 italic">
            * Inclusive of complaints of previous months resolved in the current
            month.{" "}
          </p>
          <p className="text-sm text-slate-600 mb-2 italic">
            # Inclusive of complaints pending as on the last day of the month.{" "}
          </p>
        </div>

        <div className="mt-6 rounded-lg border bg-white card-soft p-4 overflow-x-auto">
          <h3 className="font-semibold mb-2">
            Trend of annual disposal of complaints (last financial year)
          </h3>

          <table className="w-full table-auto border-collapse text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-2 border">Sr.</th>
                <th className="p-2 border text-left">Year</th>
                <th className="p-2 border text-center">Carried forward from previous year</th>
                <th className="p-2 border text-center">Received</th>
                <th className="p-2 border text-center">Resolved*</th>
                <th className="p-2 border text-center">Pending#</th>
              </tr>
            </thead>
            <tbody>
              {annualTrend.map((a, i) => (
                <tr key={a.id} className="odd:bg-white even:bg-slate-50">
                  <td className="p-2 border text-center">{i + 1}</td>
                  <td className="p-2 border">{a.year_label}</td>
                  <td className="p-2 border text-center">{a.carried}</td>
                  <td className="p-2 border text-center">{a.received}</td>
                  <td className="p-2 border text-center">{a.resolved}</td>
                  <td className="p-2 border text-center">{a.pending}</td>
                </tr>
              ))}
            </tbody>
            <tr className="bg-slate-100 font-semibold">
              <td className="p-2 border"></td>
              <td className="p-2 border">Grand Total</td>
              <td className="p-2 border text-center">
                {annualTrend.reduce((a, c) => a + Number(c.carried || 0), 0)}
              </td>
              <td className="p-2 border text-center">
                {annualTrend.reduce((a, c) => a + Number(c.received || 0), 0)}
              </td>
              <td className="p-2 border text-center">
                {annualTrend.reduce((a, c) => a + Number(c.resolved || 0), 0)}
              </td>
              <td className="p-2 border text-center">
                {annualTrend.reduce((a, c) => a + Number(c.pending || 0), 0)}
              </td>
            </tr>
          </table>
          <p className="text-sm text-slate-600 mb-2 italic">
            * Inclusive of complaints of previous months resolved in the current
            year.{" "}
          </p>
          <p className="text-sm text-slate-600 mb-2 italic">
            # Inclusive of complaints pending as on the last day of the year.{" "}
          </p>
        </div>

        {/* SUMMARY OF ANNUAL COMPLIANCE AUDIT (static - first year of registration) */}
        <div className="mt-6 rounded-lg border bg-white card-soft p-4 overflow-x-auto">
          <h3 id="compliance-audit-heading" className="font-semibold mb-2">
            Summary Of Annual Compliance Audit
          </h3>

          <table
            className="w-full table-auto border-collapse text-sm"
            aria-labelledby="compliance-audit-heading"
          >
            <thead className="bg-slate-100">
              <tr>
                <th scope="col" className="p-2 border">Sr. No.</th>
                <th scope="col" className="p-2 border text-center">Financial Year</th>
                <th scope="col" className="p-2 border text-center">Compliance Audit Status</th>
                <th scope="col" className="p-2 border text-left">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {complianceAudits.map((row, i) => (
                <tr key={row.year} className="odd:bg-white even:bg-slate-50">
                  <td className="p-2 border text-center">{i + 1}</td>
                  <td className="p-2 border text-center">{row.year}</td>
                  <td className="p-2 border text-center">{row.status}</td>
                  <td className="p-2 border">{row.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-4">
        <Accordion title="Disclosure" defaultOpen={false}>
          <div className="space-y-2 text-sm text-slate-700">
            <ul className="list-disc pl-5 space-y-1">
              <li>
                We are a SEBI Registered Investment Adviser having registration
                number - INA000020730.
              </li>
              <li>
                We are not affiliated with any other intermediaries, nor do
                receive any brokerage or commission from any third party.
              </li>
              <li>
                No penalties/directions have been issued by the SEBI under the
                SEBI Act or any other regulatory body.
              </li>
              <li>
                We do not recommend any stock broker or other intermediary to a
                client. Nor do we receive any consideration by way of
                remuneration or compensation or in any other form whatsoever
                from stock broker or other intermediary.
              </li>
              <li>
                Investments in debt instruments are subject to reinvestment
                risks as interest rates prevailing on interest amount or
                maturity due dates may differ from the original coupon of the
                bond, which might result in the proceeds being invested at a
                lower rate.
              </li>
              <li>
                If client wants opinion in the specific positions made by him
                only, we will be able to suggest appropriate view on the
                securities. Such suggestion/view under any circumstances shall
                be considered as an opinion (not advice) from our side and we
                advise client to consider our opinion and not consultancy to
                make his/her final decision. We will not be liable for any
                losses whatsoever client may incur in accepting this opinion.
              </li>
              <li>
                Aman Rakesh Shah and, its associates and employees might have
                positions or might have advised to clients which might be
                in-line with the advice made here. Aman Rakesh Shah, associates
                or their family members may or may not trade or may have
                investments in the same advice.
              </li>
              <li>
                The names of the products/nature of investments do not in any
                manner indicate their prospects or returns. The performance in
                the equity products may be adversely affected by the performance
                of individual companies, changes in the market place and
                industry specific and macro-economic factors.
              </li>
              <li>
                We do not have any association in any manner with any issuer of
                products/ securities; this ensures that there are no actual or
                potential conflicts of interest. This also ensures that
                objectivity or independence in the carrying on of investment
                advisory services is not compromised.
              </li>
              <li>
                Investment in market is subject to market risk, though best
                attempts are made for predicting markets, but no surety of
                return or accuracy of any kind is guaranteed.
              </li>
              <li>We do not guarantee any profit/return.</li>
              <li>
                The performance of the investments/products may be affected by
                changes in Government policies, general levels of interest rates
                and risks associated with trading volumes, liquidity and
                settlement systems in equity and debt markets.
              </li>
              <li>
                We do not provide any profit/loss sharing services, guaranteed
                profit services, Demat services.
              </li>
            </ul>

            <div className="mt-3">
              <strong>Disclaimers</strong>
              <ul className="list-disc pl-5 mt-2">
                <li>
                  Registration granted by SEBI, enlistment as IA with exchange
                  and certification from NISM in no way guarantee performance of
                  the intermediary or provide any assurance of returns to
                  investors.
                </li>
                <li>
                  The securities quoted, if any are for illustration only and
                  are not recommendatory.
                </li>
                <li>
                  Investments in securities market are subject to market risks.
                  Read all the related documents carefully before investing.
                </li>
              </ul>
            </div>
          </div>
        </Accordion>

        <Accordion title="Privacy Policy" defaultOpen={false}>
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              Welcome to Apar Growth Partners. We understand and respect your
              privacy, and we are committed to protecting it. In order to
              provide transparency and clarity on how we collect and use your
              information, we have created this notice which outlines our online
              information practices and your options for providing and managing
              that information.
            </p>

            <p>
              We collect certain information from you when you provide it to us
              through our website or social media. Our aim in gathering this
              information is to improve our services to you. We take great care
              to maintain the accuracy and security of this information.
            </p>

            <p>
              Protecting your privacy and data is extremely important to us. We
              take measures to ensure the security and confidentiality of any
              information shared with us. We will only use personally
              identifiable information for the purposes to which you have
              consented. However, we cannot guarantee the security of your
              information 100%, despite our efforts to protect it. Any
              information you transmit to us through our online services is done
              at your own risk.
            </p>

            <p>
              Please note that you may receive calls related to service
              promotions, contests, and pools organized by us. Your information,
              whether public or private, will be held with us only. However, it
              may be transferred or given to our regulatory authorities and
              their related organizations, as well as any organizations or
              individuals with whom we have an interest for any reason, without
              your consent.
            </p>

            <p>
              In addition of the services we provide to you, we may also use
              your personal information such as your mobile number and email
              address to send you newsletters, surveys, contest information, or
              updates on any new services that we believe may be beneficial to
              you. By subscribing to our services, you agree to allow Apar
              Growth Partners to use your personal information for these
              purposes.
            </p>

            <p>
              By submitting the 'Inquiry form' on this website or through any
              other means, including social media, you are providing your
              consent to allow us to contact you via phone call, SMS, or
              WhatsApp on the number provided by you. Even if your mobile number
              is registered on the National 'Do Not Disturb' registry, you agree
              to receive communication from us. We appreciate your trust in us
              and assure you that we will always respect your privacy.
            </p>
          </div>
        </Accordion>
      </section>
    </div>
  );
}

// import React, { useEffect, useState } from "react";
// import supabase from "../supabaseClient";

// export default function Compliance() {
//   const [loading, setLoading] = useState(true);
//   const [reportDate, setReportDate] = useState("");
//   const [monthly, setMonthly] = useState([]);
//   const [annual, setAnnual] = useState([]);

//   async function fetchData() {
//     setLoading(true);

//     const { data: report } = await supabase
//       .from("compliance_report")
//       .select("*")
//       .single();

//     if (report?.report_date) {
//       const d = new Date(report.report_date);
//       const formatted = d.toLocaleDateString("en-IN", {
//         day: "2-digit",
//         month: "long",
//         year: "numeric",
//       });
//       setReportDate(formatted);
//     }

//     const { data: m } = await supabase
//       .from("compliance_monthly")
//       .select("*")
//       .order("month_label", { ascending: false });

//     const { data: a } = await supabase
//       .from("compliance_annual")
//       .select("*")
//       .order("year_label", { ascending: false });

//     setMonthly(m || []);
//     setAnnual(a || []);
//     setLoading(false);
//   }

//   useEffect(() => {
//     fetchData();
//   }, []);

//   if (loading) return <p className="py-10 text-center">Loading...</p>;

//   return (
//     <div className="container mx-auto py-10">
//       <h1 className="text-2xl font-bold mb-2">Compliance</h1>

//       {reportDate && (
//         <p className="mb-6">
//           Data for the month ending — <strong>{reportDate}</strong>
//         </p>
//       )}

//       {/* Monthly */}
//       <div className="mb-10">
//         <h2 className="text-xl font-semibold mb-2">
//           Trend of Monthly Disposal of Complaints (Last Months)
//         </h2>

//         <table className="w-full border text-sm">
//           <thead className="bg-slate-100">
//             <tr>
//               <th className="border p-2">Month</th>
//               <th className="border p-2">Carried</th>
//               <th className="border p-2">Received</th>
//               <th className="border p-2">Resolved</th>
//               <th className="border p-2">Pending</th>
//             </tr>
//           </thead>
//           <tbody>
//             {monthly.length > 0 ? (
//               monthly.map((row) => (
//                 <tr key={row.id}>
//                   <td className="border p-2">{row.month_label}</td>
//                   <td className="border p-2">{row.carried}</td>
//                   <td className="border p-2">{row.received}</td>
//                   <td className="border p-2">{row.resolved}</td>
//                   <td className="border p-2">{row.pending}</td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td className="p-4 text-center text-gray-500" colSpan="5">
//                   No data available
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Annual */}
//       <div>
//         <h2 className="text-xl font-semibold mb-2">
//           Trend of Annual Disposal of Complaints (Last Financial Years)
//         </h2>

//         <table className="w-full border text-sm">
//           <thead className="bg-slate-100">
//             <tr>
//               <th className="border p-2">Year</th>
//               <th className="border p-2">Carried</th>
//               <th className="border p-2">Received</th>
//               <th className="border p-2">Resolved</th>
//               <th className="border p-2">Pending</th>
//             </tr>
//           </thead>
//           <tbody>
//             {annual.length > 0 ? (
//               annual.map((row) => (
//                 <tr key={row.id}>
//                   <td className="border p-2">{row.year_label}</td>
//                   <td className="border p-2">{row.carried}</td>
//                   <td className="border p-2">{row.received}</td>
//                   <td className="border p-2">{row.resolved}</td>
//                   <td className="border p-2">{row.pending}</td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td className="p-4 text-center text-gray-500" colSpan="5">
//                   No data available
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// }
